<script setup lang="ts">
import { ref, computed } from 'vue'

interface Props {
  modelValue?: number
  size?: 'md' | 'sm'
  disabled?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: number): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  size: 'md',
  disabled: false
})

const emit = defineEmits<Emits>()

const hoveredStar = ref<number | null>(null)

const currentRating = computed({
  get: () => props.modelValue,
  set: (value: number) => emit('update:modelValue', value)
})

const getStarState = (index: number) => {
  const displayValue = hoveredStar.value !== null ? hoveredStar.value : currentRating.value
  return index <= displayValue ? 'filled' : 'empty'
}

const getSvgPath = (state: 'filled' | 'empty') => {
  if (state === 'filled') {
    return 'M12 2.38419e-07L16.0174 7.98261L24 8.97391L18.5217 15.1826L20.0348 24L12 20.0348L3.96522 24L5.47826 15.1826L0 8.97391L7.98261 7.98261L12 2.38419e-07Z'
  }
  return 'M24 8.98539L16.0223 8L12 0L7.97775 7.9833L0 8.96868L5.47427 15.1816L3.95549 24L11.9833 20.0251L20.0111 24L18.4923 15.1816L23.9666 8.96868L24 8.98539ZM12 18.8727L6.4089 21.6618L7.5605 14.7808L3.33797 10.0376L9.54659 9.25261L12 4.34238L14.4534 9.25261L20.662 10.0376L16.4395 14.7808L17.5911 21.6618L12 18.8727Z'
}

const handleStarClick = (star: number) => {
  if (!props.disabled) {
    currentRating.value = star
  }
}

const handleStarHover = (star: number) => {
  if (!props.disabled) {
    hoveredStar.value = star
  }
}

const handleMouseLeave = () => {
  hoveredStar.value = null
}
</script>

<template>
  <div
    class="flex gap-0.5"
    :class="{ 'cursor-pointer': !disabled, 'opacity-50': disabled }"
    @mouseleave="handleMouseLeave"
  >
    <template v-for="i in 5" :key="i">
      <svg
        width="24"
        height="24"
        :class="[
          props.size === 'sm' ? 'size-4' : 'size-5',
          getStarState(i) === 'filled' ? 'text-orange-600' : 'text-gray-300',
          !disabled && 'transition-colors duration-150 hover:text-orange-400'
        ]"
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        @click="handleStarClick(i)"
        @mouseenter="handleStarHover(i)"
      >
        <path :d="getSvgPath(getStarState(i))" />
      </svg>
    </template>
  </div>
</template>
